import React, { useContext, useEffect, useState, useMemo, useCallback } from "react";
import { Button, Col, Row, Table, Dropdown } from "react-bootstrap";
import { useNavigate } from "react-router-dom";
import schoolApi from "../../api/schoolApi";
import { ShimmerTable } from "react-shimmer-effects";
import {
  DatatableWrapper,
  Filter,
  Pagination,
  PaginationOptions,
  TableBody,
} from "react-bs-datatable";
import { Link } from "react-router-dom";
import moment from "moment";
import { Helmet } from "react-helmet";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { usePermissions } from "../contexts/PermissionContext";
import { SessionContext } from "../SessionProvider";
import jsPDF from "jspdf";
import html2canvas from "html2canvas";
import * as XLSX from "xlsx";
import { DragDropContext, Droppable, Draggable } from "react-beautiful-dnd";
import "./DraggableDataTable.css";
import { generateAndDownloadIDCards } from "../../utils/idCardHelper";
import ScrollLazyLoader from "../common/ScrollLazyLoader";

const StudentAddmissionList = (props) => {
  const { rolePermissions, userInfo } = usePermissions();

  // select class option
  const [optionClasses, setOptionClasses] = useState([]);
  //select option
  const [optionSection, setOptionSection] = useState([]);
  const navigate = useNavigate();
  //vertical and HORIZONTAL dropdown
  const [orientation, setOrientation] = useState("vertical");
  const [body, setBody] = useState();
  const canCreate = rolePermissions?.Students_Registration_List?.can_create;
  const { selectedSessionId, currentSessionId } = useContext(SessionContext);

  // Derived state (replaces the isCurrentSession useState)
  const isCurrentSession = selectedSessionId === currentSessionId;

  // State variables for class and section selection
  const [selectedClass, setSelectedClass] = useState("");
  const [selectedSection, setSelectedSection] = useState("");
  const [selectedStatusFilter, setSelectedStatusFilter] = useState("all");
  // inactive students of the selected session (loaded only when "Inactive" is chosen)
  const [inactiveBody, setInactiveBody] = useState(null);
  const [isSectionEnabled, setIsSectionEnabled] = useState(false);
  const [filteredBody, setFilteredBody] = useState([]);
  const [selectedStudents, setSelectedStudents] = useState([]);
  // Keep selection available to the stable table cell renderer. Recreating the
  // headers on every selection causes react-bs-datatable to reset to page 1.
  const selectedStudentsRef = React.useRef(selectedStudents);
  selectedStudentsRef.current = selectedStudents;
  const [isDownloading, setIsDownloading] = useState(false);
  const [settingsList, setSettingsList] = useState([]);

  const [headerOrder, setHeaderOrder] = useState(() => {
    const savedOrder = localStorage.getItem("student_table_header_order");
    return savedOrder ? JSON.parse(savedOrder) : [
      "student_info.student_name",
      "student_info.sessions",
      "student_info.parent_phone",
      "student_info.class_name",
      "student_info.dateofbirth",
      "student_info.feemaster_type",
      "student_info.registration_no",
      "parent.name"
    ];
  });

  const [classSortOrder, setClassSortOrder] = useState("asc"); // none, asc, desc
  const [showBloodGroup, setShowBloodGroup] = useState(false);
  const [tempColor, setTempColor] = useState("#d1b2b2");
  const [textColor, setTextColor] = useState("#faf4f4");

  const [visibleColumns, setVisibleColumns] = useState(() => {
    const savedColumns = localStorage.getItem("student_table_columns");
    return savedColumns ? JSON.parse(savedColumns) : [
      "selection",
      "serial_number",
      "student_info.studentname",
      "student_info.session",
      "parent.name",
      "parent.phone",
      "student_info.class_name",
      "student_info.phone",
      "student_info.email",
      "student_info.dateofbirth",
      "student_info.registration_no",
      "student_info.createddate",
      "student_info.lastmodifieddate",
    ];
  });

  const dragItem = React.useRef(null);
  const dragOverItem = React.useRef(null);

  // =========================================================================
  // OPTIMIZED: Master Initialization useEffect (replaces 4 separate effects)
  // =========================================================================

  const applyStudentFilters = useCallback((
    sourceBody = body,
    classValue = selectedClass,
    sectionValue = selectedSection,
    statusValue = selectedStatusFilter
  ) => {
    // "Inactive" shows the inactive admissions of this session (separate list from the server)
    if ((statusValue || "").toLowerCase() === "inactive") {
      sourceBody = inactiveBody || [];
    }
    if (!sourceBody) {
      setFilteredBody([]);
      return;
    }

    const selectedClassName =
      optionClasses.find((item) => item.value === classValue)?.label || "";
    const normalizedStatusValue = (statusValue || "all").toLowerCase();

    const filtered = sourceBody.filter((student) => {
      const studentInfo = student.student_info || student;
      const className = studentInfo.class_name || "";
      const sectionName = studentInfo.section_name || "";
      const rawStatus =
        studentInfo.status ?? student?.status ?? student?.student_status ?? "";
      const normalizedStatus = String(rawStatus).trim().toLowerCase();
      const isDropoutStatus = [
        "dropout",
        "drop out",
        "drop-out",
        "dropped out",
        "droped out",
      ].includes(normalizedStatus);

      const matchesClass =
        !classValue ||
        className.trim().toLowerCase() === selectedClassName.trim().toLowerCase();
      const matchesSection =
        !sectionValue ||
        sectionName.trim().toLowerCase() === sectionValue.trim().toLowerCase();

      let matchesStatus = true;
      if (normalizedStatusValue === "dropout") {
        matchesStatus = isDropoutStatus;
      } else if (normalizedStatusValue === "present") {
        matchesStatus = !isDropoutStatus;
      }

      return matchesClass && matchesSection && matchesStatus;
    });

    if (classSortOrder !== "none") {
      filtered.sort((a, b) => {
        const getOrder = (className) => {
          if (!className) return 9999;
          
          // Clean string: remove brackets like "[A]" and extra whitespace
          let raw = className.toString().trim().toUpperCase().split("[")[0].trim();

          // 1. Pre-Primary Classes
          const prePrimary = {
            "PG": 10, "PLAY GROUP": 10, "PLAYGROUP": 10, "PLAY-GROUP": 10,
            "NUR": 20, "NURSERY": 20,
            "PREP": 30, "PRE-PRIMARY": 30, "PRE PRIMARY": 30,
            "KG": 40, "LKG": 40, "L.K.G.": 40, "JR. KG": 40, "JR KG": 40,
            "UKG": 50, "U.K.G.": 50, "SR. KG": 50, "SR KG": 50,
            "HKG": 60, "H.K.G.": 60
          };

          if (prePrimary[raw]) return prePrimary[raw];

          // Check main class and stream parts if separated by '-' or space
          let mainClassPart = raw;
          let streamPart = "";

          if (raw.includes("-")) {
            const parts = raw.split("-");
            mainClassPart = parts[0].trim();
            streamPart = parts.slice(1).join("-").trim();
          } else if (raw.includes(" ")) {
            const parts = raw.split(" ");
            if (["ARTS", "ART", "COMMERCE", "COMM", "COM", "SCIENCE", "SCI", "PCB", "PCM", "BIO", "MATH", "MATHS"].includes(parts[parts.length - 1])) {
              streamPart = parts.pop();
              mainClassPart = parts.join(" ").trim();
            }
          }

          if (prePrimary[mainClassPart]) return prePrimary[mainClassPart];

          // 2. Identify Grade Number (1 to 12+)
          let gradeNum = 0;
          const romanMap = {
            "I": 1, "FIRST": 1,
            "II": 2, "SECOND": 2,
            "III": 3, "THIRD": 3,
            "IV": 4, "FOURTH": 4,
            "V": 5, "FIFTH": 5,
            "VI": 6, "SIXTH": 6,
            "VII": 7, "SEVENTH": 7,
            "VIII": 8, "EIGHTH": 8,
            "IX": 9, "NINTH": 9,
            "X": 10, "TENTH": 10,
            "XI": 11, "ELEVENTH": 11,
            "XII": 12, "TWELFTH": 12
          };

          if (romanMap[mainClassPart]) {
            gradeNum = romanMap[mainClassPart];
          } else {
            const digitMatch = mainClassPart.match(/^(\d+)/);
            if (digitMatch) {
              gradeNum = parseInt(digitMatch[1], 10);
            }
          }

          if (gradeNum === 0) {
            return 5000 + raw.charCodeAt(0);
          }

          // 3. Stream Weight Sequence: Arts (10) -> Commerce (20) -> Science (30) -> PCB (40) -> PCM (50)
          let streamWeight = 0;
          if (streamPart) {
            const s = streamPart.toUpperCase();
            if (s.includes("ART")) {
              streamWeight = 10;
            } else if (s.includes("COMM") || s.includes("COM")) {
              streamWeight = 20;
            } else if (s === "SCI" || s === "SCIENCE") {
              streamWeight = 30;
            } else if (s.includes("PCB") || s.includes("BIO")) {
              streamWeight = 40;
            } else if (s.includes("PCM") || s.includes("MATH")) {
              streamWeight = 50;
            } else {
              streamWeight = 60 + (s.charCodeAt(0) % 30);
            }
          }

          return 100 + (gradeNum * 100) + streamWeight;
        };

        const getClass = (student) => (student?.student_info || student)?.class_name;
        const getSection = (student) => (student?.student_info || student)?.section_name;
        const getStudentName = (student) => ((student?.student_info || student)?.studentname || (student?.student_info || student)?.firstname || "").toString();

        const orderA = getOrder(getClass(a));
        const orderB = getOrder(getClass(b));
        let diff = orderA - orderB;
        if (diff === 0) {
          const secA = (getSection(a) || "").toString();
          const secB = (getSection(b) || "").toString();
          diff = secA.localeCompare(secB);
        }
        if (diff === 0) {
          diff = getStudentName(a).localeCompare(getStudentName(b));
        }

        return classSortOrder === "asc" ? diff : -diff;
      });
    }

    // Force a new array reference by mapping or spreading to ensure react-bs-datatable detects it
    setFilteredBody([...filtered]);
    setSelectedStudents([]);
  }, [body, inactiveBody, optionClasses, selectedClass, selectedSection, selectedStatusFilter, classSortOrder]);

  
  useEffect(() => {
    let isMounted = true;

    async function initializeData() {
      try {
        // Fetch all independent APIs concurrently
        const [settingsResult, studentResult, classResult] = await Promise.all([
          schoolApi.getAllSetting(),
          schoolApi.fetchStudentAddmission(selectedSessionId),
          schoolApi.getActiveClassRecords(),
        ]);

        if (!isMounted) return;

        // 1. Process Settings
        if (settingsResult?.success && settingsResult?.data) {
          setSettingsList(settingsResult.data);
          const bloodGroupSetting = settingsResult.data.find((s) => s.key === "blood_group");
          const colorSetting = settingsResult.data.find((s) => s.key === "id_card_color");
          const textColorSetting = settingsResult.data.find((s) => s.key === "id_card_text_color");

          if (bloodGroupSetting) setShowBloodGroup(bloodGroupSetting.value === "true");
          if (colorSetting) setTempColor(colorSetting.value || "#d1b2b2");
          if (textColorSetting) setTextColor(textColorSetting.value || "#faf4f4");
          const activeOrientSetting = settingsResult.data.find((s) => s.key === "id_card_active_orientation");
          if (activeOrientSetting && activeOrientSetting.value) setOrientation(activeOrientSetting.value);
        }

        // 2. Process Classes
        if (classResult?.result) {
          setOptionClasses(
            classResult.result.map((item) => ({
              value: item.id,
              label: item.classname,
            }))
          );
        } else {
          setOptionClasses([]);
        }

        // 3. Process Students & Dynamic Header Setup
        if (studentResult?.success) {
          const newBody = studentResult.studentAddmision || [];
          const normalizedBody = newBody.map((row) => {
            const studentInfo = row.student_info || row;
            const parentName =
              studentInfo.parentname || studentInfo.parent_name || "";

            return {
              ...row,
              parent: {
                ...row.parent,
                name: parentName,
              },
            };
          });

          setBody(normalizedBody);
          setFilteredBody(normalizedBody);

          // Build header order dynamically without triggering a cascading re-render
          if (normalizedBody.length > 0) {
            const sampleStudent = normalizedBody[0].student_info || normalizedBody[0];
            const rawKeys = Object.keys(sampleStudent);

            // Reconstruct base props manually here to avoid strict dependency on baseHeader outside
            const existingProps = [
              "selection", "student_info.studentname", "student_info.session",
              "parent.name", "parent.phone", "student_info.class_name",
              "student_info.phone", "student_info.email", "student_info.dateofbirth",
              "student_info.registration_no", "student_info.createddate",
              "student_info.lastmodifieddate"
            ];

            const newDynamicProps = rawKeys
              .map((key) => (normalizedBody[0].student_info ? `student_info.${key}` : key))
              .filter((prop) => !existingProps.includes(prop));

            const allExpectedProps = [...existingProps.filter(p => p !== "selection"), ...newDynamicProps];

            setHeaderOrder((prev) => {
              const missing = allExpectedProps.filter((prop) => !prev.includes(prop));
              if (prev.length === 0) return allExpectedProps;
              return [...prev, ...missing];
            });
          }
        }
      } catch (error) {
        if (isMounted) {
          setBody([]);
          setFilteredBody([]);
          setOptionClasses([]);
          toast.error(error?.response?.data?.message || "Failed to load data");
        }
      }
    }

    if (selectedSessionId) {
      initializeData();
    }

    return () => {
      isMounted = false;
    };
  }, [selectedSessionId]);

  useEffect(() => {
    if (visibleColumns.length > 0) {
      localStorage.setItem("student_table_columns", JSON.stringify(visibleColumns));
    }
    if (headerOrder.length > 0) {
      localStorage.setItem("student_table_header_order", JSON.stringify(headerOrder));
    }
  }, [visibleColumns, headerOrder]);

  useEffect(() => {
    if (body) {
      applyStudentFilters(body, selectedClass, selectedSection, selectedStatusFilter);
    }
  }, [body, inactiveBody, selectedClass, selectedSection, selectedStatusFilter, classSortOrder]);

  // load inactive students of the current session when "Inactive" is selected
  useEffect(() => {
    if (selectedStatusFilter !== "inactive" || !selectedSessionId) return;
    let alive = true;
    (async () => {
      try {
        const res = await schoolApi.fetchStudentAddmission(selectedSessionId, undefined, "inactive");
        if (alive) setInactiveBody(res?.studentAddmision || []);
      } catch (e) {
        if (alive) setInactiveBody([]);
      }
    })();
    return () => { alive = false; };
  }, [selectedStatusFilter, selectedSessionId]);


  // const handleColumnDragEnd = (result) => {
  //   const { source, destination } = result;

  //   if (!destination) return;
  //   if (source.index === destination.index) return;

  //   const newVisibleColumns = Array.from(visibleColumns);
  //   const [removed] = newVisibleColumns.splice(source.index, 1);
  //   newVisibleColumns.splice(destination.index, 0, removed);

  //   setVisibleColumns(newVisibleColumns);
  //   setHeaderOrder(newVisibleColumns.filter((col) => col !== "selection"));
  //   toast.info("Column reordered successfully");
  // };


  const handleColumnDragEnd = (result) => {
    const { source, destination } = result;
    if (!destination) return;
    if (source.index === destination.index) return;

    // Reorder visible columns
    const newVisibleColumns = Array.from(visibleColumns);
    const [removed] = newVisibleColumns.splice(source.index, 1);
    newVisibleColumns.splice(destination.index, 0, removed);
    setVisibleColumns(newVisibleColumns);

    // ✅ FIX: rebuild headerOrder by splicing the reordered visible cols
    // back into the FULL headerOrder, preserving hidden columns in their positions
    const hiddenCols = headerOrder.filter(col => !visibleColumns.includes(col));
    const newHeaderOrder = [
      ...newVisibleColumns.filter(col => col !== "selection"),
      ...hiddenCols,
    ];
    setHeaderOrder(newHeaderOrder);
    toast.info("Column reordered successfully");
  };

  const handleClassChange = async (e) => {
    const selectedClassValue = e.target.value;
    setSelectedClass(selectedClassValue);

    if (selectedClassValue === "") {
      setIsSectionEnabled(false);
      setSelectedSection("");
      setOptionSection([]);
      applyStudentFilters(body, "", "", selectedStatusFilter);
    } else {
      setIsSectionEnabled(true);
      setSelectedSection("");

      try {
        let sectionList = await schoolApi.getSectionRecordById(selectedClassValue);

        if (sectionList?.record && sectionList?.record?.length > 0) {
          let sectionOptions = sectionList.record.map((item) => ({
            value: item.name,
            label: item.name,
          }));
          setOptionSection(sectionOptions);
        } else {
          setOptionSection([]);
        }
      } catch (error) {
        setOptionSection([]);
      }

      applyStudentFilters(body, selectedClassValue, "", selectedStatusFilter);
    }
  };

  const handleSectionChange = (e) => {
    const selectedSectionValue = e.target.value;
    setSelectedSection(selectedSectionValue);
    applyStudentFilters(body, selectedClass, selectedSectionValue, selectedStatusFilter);
  };

  const handleStatusFilterChange = (e) => {
    const value = e.target.value;
    setSelectedStatusFilter(value);
    applyStudentFilters(body, selectedClass, selectedSection, value);
  };

  const handleSelectStudent = (studentId) => {
    if (!studentId) return;
    setSelectedStudents((prev) => {
      if (prev.includes(studentId)) {
        return prev.filter((id) => id !== studentId);
      } else {
        return [...prev, studentId];
      }
    });
  };

  const handleSelectAll = () => {
    if (selectedStudents.length === filteredBody.length && filteredBody.length > 0) {
      setSelectedStudents([]);
    } else {
      const allIds = filteredBody.map((student) =>
        student.student_info?.student_addmission_id || student.student_info?.id || student.id
      ).filter(Boolean);

      setSelectedStudents(allIds);
    }
  };
  const getActiveOrientation = () => {
    return settingsList.find((s) => s.key === "id_card_active_orientation")?.value || orientation || "vertical";
  };

  const getDynamicSchemaForOrientation = (targetOrientation) => {
    const settingKey = targetOrientation === "vertical" ? "id_card_template_vertical" : "id_card_template_horizontal";
    const foundTplSetting = settingsList.find((s) => s.key === settingKey);
    if (foundTplSetting && foundTplSetting.value) {
      try {
        const parsed =
          typeof foundTplSetting.value === "string" && foundTplSetting.value.trim().startsWith("{")
            ? JSON.parse(foundTplSetting.value)
            : foundTplSetting.value;
        if (parsed && Array.isArray(parsed.elements) && parsed.elements.length > 0) {
          return parsed;
        }
      } catch (e) {
        console.warn("Could not parse dynamic card template:", e);
      }
    }
    return null;
  };

  const downloadIDCards = async (mode) => {
    const currentOrientation = typeof mode === "string" && (mode === "vertical" || mode === "horizontal") ? mode : getActiveOrientation();
    let studentsToDownload = [];

    if (selectedStudents.length > 0) {
      studentsToDownload = filteredBody.filter((student) =>
        selectedStudents.includes(student.student_info.student_addmission_id)
      );
    } else {
      studentsToDownload = filteredBody;
    }
    if (studentsToDownload.length === 0) {
      toast.warning("No students selected to download!");
      return;
    }
    setIsDownloading(true);
    try {
      const dynamicSchema = getDynamicSchemaForOrientation(currentOrientation);
      await generateAndDownloadIDCards({
        students: studentsToDownload,
        orientation: currentOrientation,
        dynamicSchema,
        tempColor,
        textColor,
        showBloodGroup,
        userInfo,
      });
      setSelectedStudents([]);
    } catch (error) {
      console.error("Error:", error);
      toast.error("Failed to generate ID cards");
    } finally {
      setIsDownloading(false);
    }
  };

  const downloadSingleStudentCard = async (row) => {
    const currentOrientation = getActiveOrientation();
    setIsDownloading(true);
    try {
      const dynamicSchema = getDynamicSchemaForOrientation(currentOrientation);
      await generateAndDownloadIDCards({
        students: [row],
        orientation: currentOrientation,
        dynamicSchema,
        tempColor,
        textColor,
        showBloodGroup,
        userInfo,
      });
    } catch (error) {
      console.error("Error generating single card:", error);
      toast.error("Failed to generate single student ID card");
    } finally {
      setIsDownloading(false);
    }
  };

  const renderProfileImage = (studentInfo) => {
    if (!studentInfo?.file_id) {
      return `
      <img
        src="/uploads/dwps_ajmer/image_2024102442318.png"
        alt="dummy_profile"
        style="width:100%; height:100%; object-fit:cover;"
      />
    `;
    }

    const ts = Date.now();
    const pid = studentInfo.file_parent_id;
    const fid = studentInfo.file_id;
    const ftype = studentInfo.file_type;
    const tenantcode = userInfo?.tenantcode;

    return `
    <img
      src="/uploads/${tenantcode}/${pid}/${fid}.${ftype}?v=${ts}"
      alt="profile"
      style="width:100%; height:100%; object-fit:cover;"
      onerror="
        if(!this.dataset.step){
          this.dataset.step='1';
          this.src='/uploads/${pid}/${fid}.${ftype}?v=${ts}';
        }else{
          this.onerror=null;
          this.src='/uploads/dwps_ajmer/image_2024102442318.png';
        }
      "
    />
  `;
  };


  const handleDownloadReport = async () => {
    let studentsToDownload = [];

    // Check if any student is selected
    if (selectedStudents.length > 0) {
      studentsToDownload = filteredBody.filter((student) =>
        selectedStudents.includes(student.student_info.student_addmission_id),
      );
    } else {
      studentsToDownload = filteredBody;
    }

    if (studentsToDownload.length === 0) {
      toast.warning("No students selected to download!");
      return;
    }

    setIsDownloading(true);

    try {
      console.log(" Students to download:", studentsToDownload.length);

      //  Method 1: Create separate canvas for each page 
      const pdf = new jsPDF({
        orientation: "portrait",
        unit: "mm",
        format: "a4",
      });

      let isFirstPage = true;

      // Loop through each student
      for (let i = 0; i < studentsToDownload.length; i++) {
        const student = studentsToDownload[i];
        const studentInfo = student.student_info;
        const parentInfo = student.parent_info || null;

        console.log(` Generating Student ${i + 1}:`, studentInfo?.firstname, studentInfo?.lastname);

        // ==================== PAGE 1 FOR THIS STUDENT ====================
        const page1 = document.createElement("div");
        page1.style.cssText = `
              width: 210mm;
              min-height: 297mm;
              background: white;
              padding: 15mm 10mm;
              box-sizing: border-box;
              font-family: Arial, sans-serif;
            `;

        const tableField = (label, value) => {
          let displayValue = "&nbsp;";

          if (
            value &&
            value !== "null" &&
            value !== "undefined" &&
            value !== ""
          ) {
            displayValue = value;
          }

          return `
                  <div style="
                    margin-bottom:10px;
                    border-bottom:1px solid #e0e0e0;
                    padding-bottom:2px;
                   
                  ">
                    <div style="
                      font-weight:bold;
                      font-size:10px;
                      color:#666;
                      margin-bottom:3px;
                    ">
                      ${label}
                    </div>

                    <div style="
                       font-size:10px;
                        line-height:14px;
                        min-height:28px;
                        display:-webkit-box;
                        -webkit-line-clamp:2;
                        -webkit-box-orient:vertical;
                        overflow:hidden;
 
                    ">
                      ${displayValue}
                    </div>
                  </div>
                `;
        };
        // Header for Page 1
        page1.innerHTML = `
               <div style="
                  display:flex;
                  align-items:center;
                  justify-content:space-between;
                  
                ">
                  
                  <!-- Left Logo -->
                  <div style="
                    width:100px;
                    height:100px;
                    flex-shrink:0;
                  ">
                    <img
                      src="/${userInfo?.tenantcode}-sidebar-logo.png"
                      alt="Company Logo"
                      style="
                        width:100%;
                        height:100%;
                        object-fit:cover;
                      "
                      onerror="this.src='/uploads/dwps_ajmer/image_2024102442318.png'"
                    />
                  </div>

                  <!-- Center Content -->
                  <div style="
                    flex:1;
                    text-align:center;
                  ">
                    <h2 style="
                      color:#007bff;
                      margin:0;
                      font-size:22px;
                    ">
                      ${userInfo?.companyname || "Student 360° Report"}
                    </h2>

                    <p style="margin-top:8px;">
                      ${userInfo?.companystreet || " "}
                      ${userInfo?.companystate || " "}
                      ${userInfo?.companypincode || " "}
                    </p>

                    <p style="
                      margin:5px 0 0 0;
                      font-size:12px;
                    ">
                      Complete Student Information Report
                    </p>

                    <small style="
                      font-size:10px;
                      color:#666;
                    ">
                      Generated on: ${new Date().toLocaleString()}
                    </small>
                  </div>

                  <!-- Right Empty Space -->
                  <div style="width:100px;"></div>

                </div>
            `;

        // Student Information Table
        let studentTable = `
              <h5 style="background:#6c757d; color:white; padding:6px 8px; border-radius:4px; margin:8px 0; margin-top:30px;">
                Student Information
              </h5>
              <table style="width:100%; border-collapse:collapse;">
                <tr>
                  <td style="width:33%; vertical-align:top; padding:40px;  ">
                    <div style="text-align:center; margin-bottom:38px;">
                      <div style="width:100px; height:100px; border-radius:50%; overflow:hidden; border:2px solid #0077b6; margin:auto;">
                        ${renderProfileImage(studentInfo)}
                      </div>
                    </div>
                  ${tableField("AADHAR NUMBER", studentInfo?.adharnumber || " ")}
                  ${tableField("SESSION", studentInfo?.session_year || " ")}

                  ${tableField("CATEGORY", studentInfo?.category || " ")}


                  ${tableField("FEES TYPE", studentInfo?.fee_master_type || " ")}
                  ${tableField("GENDER", studentInfo?.gender || " ")}
                  
                  </td>
                  <td style="width:33%; vertical-align:top; padding:4px;">
                  ${tableField("SERIAL NO", studentInfo?.srno || " ")}

                  ${tableField("GENDER", studentInfo?.gender || " ")}
                

                  ${tableField("CLASS NAME", studentInfo?.class_name || " ")}
                  ${tableField("TOTAL DUES", studentInfo?.total_dues || " ")}
                  ${tableField("FORM NUMBER", studentInfo?.formno || " ")}
                  ${tableField("REGISTRATION NUMBER", studentInfo?.registration_no || " ")}
                  ${tableField("SECTION NAME", studentInfo?.section_name || " ")}
                   ${tableField("RELIGION", studentInfo?.religion || " ")}
                  </td>
                  <td style="width:33%; vertical-align:top; padding:4px; ">
                    ${tableField("STUDENT NAME", `${studentInfo?.firstname || ""} ${studentInfo?.lastname || ""}`)}
                    ${tableField("DATE OF BIRTH", studentInfo?.dateofbirth || " ")}
                    ${tableField("EMAIL", studentInfo?.email || " ")}
                    ${tableField("STATUS", studentInfo?.status || " ")}
                    ${tableField("TOTAL FEE", studentInfo?.total_fee || " ")}
                    ${tableField("PHONE", studentInfo?.phone || " ")}
                    ${tableField("CITY", studentInfo?.city || " ")} 
                  </td>
                </tr>
              </table>
            `;

        page1.innerHTML += studentTable;

        // --- CHANGE 1: Parent Information Added to Page 1 ---
        if (parentInfo) {
          let parentHTML = `
                <h5 style="background:#6c757d; color:white; padding:6px 8px; border-radius:4px; ">
                  Parent Information
                </h5>
                <table style="width:100%; border-collapse:collapse; margin:0px 10px 0px 25px;">
                  <tr><td colspan="4"><h5 style="margin:4px 0; margin-top:20px;">Father Detail :</h5></td></tr>
                  <tr>
                    <td width="25%">${tableField("FATHER NAME", `${parentInfo?.Parent_Father?.firstname || " "} ${parentInfo?.Parent_Father?.lastname || " "}`)}</td>
                    <td width="25%">${tableField("FATHER PHONE", parentInfo?.Parent_Father?.phone || " ")}</td>
                    <td width="25%">${tableField("FATHER EMAIL", parentInfo?.Parent_Father?.email || " ")}</td>
                    <td width="25%">${tableField("FATHER OCCUPATION", parentInfo?.Parent_Father?.occupation || " ")}</td>
                  </tr>
                  <tr><td colspan="4"><h5 style="margin:4px 0; margin-top:20px;">Mother Detail :</h5></td></tr>
                  <tr>
                    <td>${tableField("MOTHER NAME", `${parentInfo?.Parent_Mother?.firstname || " "} ${parentInfo?.Parent_Mother?.lastname || " "}`)}</td>
                    <td>${tableField("MOTHER PHONE", parentInfo?.Parent_Mother?.phone || " ")}</td>
                    <td>${tableField("MOTHER EMAIL", parentInfo?.Parent_Mother?.email || " ")}</td>
                    <td>${tableField("MOTHER OCCUPATION", parentInfo?.Parent_Mother?.occupation || " ")}</td>
                  </tr>
                  <tr><td colspan="4"><h5 style="margin:4px 0; margin-top:20px;">Guardian Detail :</h5></td></tr>
                  <tr>
                    <td>${tableField("GUARDIAN NAME", `${parentInfo?.Parent_Guardian?.firstname || " "} ${parentInfo?.Parent_Guardian?.lastname || " "}`)}</td>
                    <td>${tableField("GUARDIAN PHONE", parentInfo?.Parent_Guardian?.phone || " ")}</td>
                    <td>${tableField("GUARDIAN EMAIL", parentInfo?.Parent_Guardian?.email || " ")}</td>
                    <td>${tableField("GUARDIAN OCCUPATION", parentInfo?.Parent_Guardian?.occupation || " ")}</td>
                  </tr>
                </table>
              `;
          page1.innerHTML += parentHTML;
        }
        // --- END OF CHANGE 1 ---

        document.body.appendChild(page1);

        page1.style.position = "fixed";
        page1.style.left = "-9999px";
        page1.style.top = "0";

        // Capture Page 1
        // await new Promise(resolve => setTimeout(resolve, 500));
        const canvas1 = await html2canvas(page1, {
          scale: 1.5,
          backgroundColor: "#ffffff",
          useCORS: true,
          logging: false,
        });

        const imgData1 = canvas1.toDataURL("image/jpeg", 0.9);
        const imgWidth = 210;
        const imgHeight1 = (canvas1.height * imgWidth) / canvas1.width;

        if (isFirstPage) {
          pdf.addImage(imgData1, "JPEG", 0, 0, imgWidth, imgHeight1);
          isFirstPage = false;
        } else {
          pdf.addPage();
          pdf.addImage(imgData1, "JPEG", 0, 0, imgWidth, imgHeight1);
        }

        document.body.removeChild(page1);

        // ==================== PAGE 2 FOR THIS STUDENT ====================
        const page2 = document.createElement("div");
        page2.style.cssText = `
              width: 210mm;
              min-height: 297mm;
              background: white;
              padding: 15mm 10mm;
              box-sizing: border-box;
              font-family: Arial, sans-serif;
            `;

        // --- CHANGE 2: Page 2 Header - Only School Name, No Student Name ---
        // page2.innerHTML = `
        //   <div style="text-align: center; margin-bottom: 20px; padding-bottom: 10px; border-bottom: 2px solid #007bff;">
        //     <h2 style="color: #007bff; margin: 0; font-size: 18px;">${userInfo?.companyname || 'Student 360° Report'}</h2>
        //     <p style="margin: 3px 0; font-size: 10px;">Complete Student Information Report - Page 2 of 2</p>
        //     <p style="margin: 3px 0; font-size: 9px; color: #666;">Generated: ${new Date().toLocaleString()}</p>
        //     <!-- Student name line removed as requested -->
        //   </div>
        // `;
        // --- END OF CHANGE 2 ---

        // Address Section (Only on Page 2)
        page2.innerHTML += `
              <h5 style="background:#6c757d; color:white; padding:6px 8px; border-radius:4px; margin:8px 0;">
                Address Information
              </h5>
              <table style="width:100%; border-collapse:collapse; margin:0px 10px 0px 25px;">
                <tr><td colspan="4"><h5 style="margin:4px 0; margin-top:20px;">Current Address :</h5></td></tr>
                <tr>
                  <td width="25%">${tableField("STREET", studentInfo?.street || " ")}</td>
                  <td width="25%">${tableField("CITY", studentInfo?.city || " ")}</td>
                  <td width="25%">${tableField("STATE", studentInfo?.state || " ")}</td>
                  <td width="25%">${tableField("PINCODE", studentInfo?.pincode || " ")}</td>
                </tr>
                <tr><td colspan="4"><h5 style="margin:4px 0; margin-top:20px;">Permanent Address :</h5></td></tr>
                <tr>
                  <td>${tableField("PERMANENT STREET", studentInfo?.permanentstreet || " ")}</td>
                  <td>${tableField("PERMANENT CITY", studentInfo?.permanentcity || " ")}</td>
                  <td>${tableField("PERMANENT STATE", studentInfo?.permanentstate || " ")}</td>
                  <td>${tableField("PERMANENT PINCODE", studentInfo?.permanentpostalcode || " ")}</td>
                </tr>
              </table>
            `;

        // --- CHANGE 3: Footer Added on Page 2 ---
        page2.innerHTML += `
              <div style="text-align: center; margin-top: 30px; padding-top: 8px; border-top: 1px solid #ddd; font-size: 8px; color: #999;">
                © ${new Date().getFullYear()} ${userInfo?.companyname || 'School System'} | All Rights Reserved
              </div>
            `;
        // --- END OF CHANGE 3 ---

        document.body.appendChild(page2);

        page2.style.position = "fixed";
        page2.style.left = "-9999px";
        page2.style.top = "0";

        // await new Promise(resolve => setTimeout(resolve, 500));

        const canvas2 = await html2canvas(page2, {
          scale: 1.5,
          backgroundColor: "#ffffff",
          useCORS: true,
          logging: false,
        });

        const imgData2 = canvas2.toDataURL("image/jpeg", 0.9);
        const imgHeight2 = (canvas2.height * imgWidth) / canvas2.width;

        pdf.addPage();
        pdf.addImage(imgData2, "JPEG", 0, 0, imgWidth, imgHeight2);

        document.body.removeChild(page2);

        console.log(`Student ${i + 1} pages added to PDF`);
      }

      // Add page numbers
      const totalPages = pdf.internal.getNumberOfPages();
      for (let i = 1; i <= totalPages; i++) {
        pdf.setPage(i);
        pdf.setFontSize(8);
        pdf.setTextColor(100, 100, 100);
        pdf.text(`Page ${i} of ${totalPages}`, 105, 287, { align: "center" });
      }

      const fileName = `Students_360_Report_${moment().format("DD-MM-YYYY")}.pdf`;
      pdf.save(fileName);

      setSelectedStudents([]);
      toast.success(` Downloaded ${studentsToDownload.length} student(s) 360 report`);

    } catch (error) {
      console.error(" PDF Generation Error:", error);
      toast.error("Failed to generate 360 report: " + error.message);
    } finally {
      setIsDownloading(false);
    }
  };
  const baseHeader = [
    {
      title: (
        <input
          type="checkbox"
          checked={
            filteredBody.length > 0 &&
            selectedStudents.length === filteredBody.length
          }
          onChange={handleSelectAll}
          style={{ cursor: "pointer", width: "18px", height: "18px" }}
        />
      ),
      displayName: "Checkboxes",
      prop: "selection",
      cell: (row) => {
        // FIX: Fallback to whatever ID key exists on the record safely
        const studentId = row.student_info?.student_addmission_id || row.student_info?.id || row.id;
        return (
          <div className="wasim">
            <input
              type="checkbox"
              checked={selectedStudentsRef.current.includes(studentId)}
              onChange={() => handleSelectStudent(studentId)}
              style={{ cursor: "pointer", width: "18px", height: "18px" }}
            />
          </div>
        );
      },
    },
    {
      title: <span className="me-5 ">Student Name</span>,
      displayName: "Student Name",
      prop: "student_info.studentname",
      isFilterable: true,
      cell: (row) => (
        <Link to={"/studentaddmissions/" + row.student_info.student_addmission_id} state={row}>
          {row.student_info.studentname}
        </Link>
      ),
    },
    { title: <span className="ms-2">Session</span>, displayName: "Session", prop: "student_info.session", isFilterable: true },
    {
      title: "Parent", // UI Header Text Fix
      displayName: "Parent Name",
      prop: "parent.name",
      isFilterable: true,
      cell: (row) => {
        const student = row.student_info || row;
        const parentObj = student.Parent_Father || student.Parent_Mother || student.Parent_Guardian || student.parent || student.Parent || null;
        if (parentObj && (parentObj.firstname || parentObj.lastname)) return `${parentObj.firstname || ""} ${parentObj.lastname || ""}`.trim();
        return student.parentname || student.parent_name || "";
      },
    },
    {
      title: "Parent Phone",
      displayName: "Parent Phone",
      prop: "parent.phone",
      isFilterable: true,
      cell: (row) => {
        const student = row.student_info || row;
        const parentObj = student.Parent_Father || student.Parent_Mother || student.Parent_Guardian || student.parent || student.Parent || null;
        return (parentObj && (parentObj.phone || parentObj.mobile)) || student.parentmobile || student.parent_phone || student.phone || "";
      },
    },
    {
      title: "Class Name",
      displayName: "Class Name",
      prop: "student_info.class_name",
      cell: (row) => `${row.student_info.class_name} [${row.student_info.section_name}]`,
      isFilterable: true,
    },
    { title: "Phone", displayName: "Phone", prop: "student_info.phone", isFilterable: true },
    { title: "Email", displayName: "Email", prop: "student_info.email", isFilterable: true },
    {
      title: "Date Of Birth",
      displayName: "Date Of Birth",
      prop: "student_info.dateofbirth",
      isFilterable: true,
      cell: (row) => moment(row.student_info.dateofbirth).format("DD-MM-YYYY"),
    },
    { title: "Registration ID", displayName: "Registration ID", prop: "student_info.registration_no", isFilterable: true },
    {
      title: "Created Date",
      displayName: "Created Date",
      prop: "student_info.createddate",
      isFilterable: true,
      cell: (row) => row.student_info?.createddate
        ? moment(row.student_info.createddate).format("DD-MM-YYYY HH:mm")
        : "-",
    },
    {
      title: "Last Modified Date",
      displayName: "Last Modified Date",
      prop: "student_info.lastmodifieddate",
      isFilterable: true,
      cell: (row) => row.student_info?.lastmodifieddate
        ? moment(row.student_info.lastmodifieddate).format("DD-MM-YYYY HH:mm")
        : "-",
    },
  ];

  const allHeaders = useMemo(() => {
    let headersList = [...baseHeader];

    if (body && body.length > 0) {
      const sampleStudent = body[0].student_info || body[0];
      const rawKeys = Object.keys(sampleStudent);
      const existingProps = baseHeader.map(h => h.prop);

      const dynamicHeaders = rawKeys.map(key => {
        const propPath = body[0].student_info ? `student_info.${key}` : key;

        if (existingProps.includes(propPath)) return null;

        const formattedTitle = key.replace(/_/g, ' ').toUpperCase();
        return {
          title: formattedTitle,
          displayName: formattedTitle,
          prop: propPath,
          isFilterable: true,
          cell: (row) => {
            const val = propPath.split('.').reduce((o, i) => (o ? o[i] : null), row);
            return val !== null && val !== undefined && val !== "" ? val.toString() : "-";
          }
        };
      }).filter(Boolean);

      headersList = [...headersList, ...dynamicHeaders];
    }
    return headersList;
  }, [body]);

  const hasVisibleDataColumns = visibleColumns.filter((col) => col !== "selection").length > 0;
  const tableHeaders = useMemo(
    () =>
      hasVisibleDataColumns
        ? visibleColumns.map((colProp) => allHeaders.find((header) => header.prop === colProp)).filter(Boolean)
        : [],
    [allHeaders, hasVisibleDataColumns, visibleColumns]
  );

  const toggleColumn = (prop) => {
    setVisibleColumns((prev) => {
      if (prev.includes(prop)) {
        return prev.filter((col) => col !== prop);
      }
      return ["selection", ...headerOrder.filter((h) => [...prev, prop].includes(h))];
    });
  };

  const handleShuffleColumns = () => {
    // ✅ FIX: shuffle the FULL headerOrder, not just visible subset
    const allProps = headerOrder; // already contains hidden cols
    const shuffled = [...allProps].sort(() => Math.random() - 0.5);
    setHeaderOrder(shuffled);
    setVisibleColumns([
      "selection",
      ...shuffled.filter((prop) => visibleColumns.includes(prop)),
    ]);
    toast.success("Columns shuffled successfully");
  };


  // const handleShuffleColumns = () => {
  //   const shuffled = [...headerOrder].sort(() => Math.random() - 0.5);
  //   setHeaderOrder(shuffled);
  //   setVisibleColumns([
  //     "selection",
  //     ...shuffled.filter((prop) => visibleColumns.includes(prop)),
  //   ]);
  //   toast.success("Columns shuffled successfully");
  // };

  // =========================================================================
  // GLOBAL TEXT EXTRACTION HELPERS FOR ALL REPORT GENERATORS
  // =========================================================================

  // Safely extracts raw text from complex React elements (like your Link component)
  const extractText = (node) => {
    if (node === null || node === undefined) return "";
    if (typeof node === "string" || typeof node === "number") return String(node);
    if (Array.isArray(node)) return node.map(extractText).join("");
    if (node.props && node.props.children) return extractText(node.props.children);
    return "";
  };

  // Maps row data seamlessly handling custom cell functions and complex objects
  const getRowData = (row, h, index) => {
    if (h.prop === 'serial_number') return String(index + 1);

    let val = "";
    if (typeof h.cell === "function") {
      val = extractText(h.cell(row));
    } else {
      val = h.prop.split('.').reduce((o, i) => (o ? o[i] : null), row);
      if (h.prop === "student_info.class_name") val = `${row.student_info?.class_name || ""} [${row.student_info?.section_name || ""}]`;
      if (h.prop === "student_info.dateofbirth" && val) val = moment(val).format("DD-MM-YYYY");
    }
    return val !== null && val !== undefined && val !== "" ? String(val).trim() : "";
  };


  const downloadReport = () => {
    const pdf = new jsPDF("p", "mm", "a4");
    const pageWidth = pdf.internal.pageSize.getWidth();
    const pageHeight = pdf.internal.pageSize.getHeight();
    let y = 20;
    const rowHeight = 10;

    let studentsToReport = selectedStudents.length > 0
      ? filteredBody.filter((student) => selectedStudents.includes(student.student_info.student_addmission_id))
      : filteredBody;

    if (studentsToReport.length === 0) return toast.warning("No data to export");

    const activeUiColumns = headerOrder
      .filter((prop) => visibleColumns.includes(prop))
      .map((prop) => allHeaders.find((h) => h.prop === prop))
      .filter(Boolean);

    if (activeUiColumns.length === 0) return toast.warning("No columns selected to display");

    const startX = 10;
    const usableWidth = pageWidth - 20;

    let totalWeight = 0;
    const columnConfigs = activeUiColumns.map((col) => {
      let weight = 1;
      const prop = col.prop;

      if (prop.includes("studentname") || prop.includes("email") || prop.includes("parent.name")) {
        weight = 1.8;
      } else if (prop.includes("registration_no") || prop.includes("phone")) {
        weight = 1.2;
      }
      totalWeight += weight;
      return { col, weight };
    });

    let currentX = startX;
    const pdfColumns = columnConfigs.map((cfg) => {
      const colWidth = (cfg.weight / totalWeight) * usableWidth;
      const config = {
        h: cfg.col,
        // Header title formatting logic specifically tailored to match table UI
        title: (typeof cfg.col.title === "string" ? cfg.col.title : cfg.col.displayName) || cfg.col.prop,
        x: currentX,
        width: colWidth,
      };
      currentX += colWidth;
      return config;
    });

    const drawHeader = () => {
      pdf.setFont("helvetica", "bold");
      pdf.setFontSize(16);
      pdf.text("STUDENT REPORT", pageWidth / 2, 12, { align: "center" });

      pdf.setFont("helvetica", "normal");
      pdf.setFontSize(9);
      pdf.text(`Date: ${new Date().toLocaleDateString()}`, 10, 20);
      pdf.text(`Total Students: ${studentsToReport.length}`, pageWidth - 50, 20);

      y = 26;
      pdf.setFont("helvetica", "bold");
      pdf.setFontSize(8);

      pdfColumns.forEach((col) => {
        pdf.rect(col.x, y, col.width, rowHeight);
        pdf.text(col.title, col.x + 2, y + 6, { maxWidth: col.width - 4 });
      });

      y += rowHeight;
    };

    drawHeader();

    studentsToReport.forEach((student, index) => {
      if (y > pageHeight - 20) {
        pdf.addPage();
        drawHeader();
      }

      pdf.setFont("helvetica", "normal");
      pdf.setFontSize(7.5);

      pdfColumns.forEach((col) => {
        let cellText = getRowData(student, col.h, index) || "-";
        pdf.rect(col.x, y, col.width, rowHeight);
        pdf.text(cellText, col.x + 2, y + 6, {
          maxWidth: col.width - 4,
        });
      });

      y += rowHeight;
    });

    const totalPages = pdf.internal.getNumberOfPages();
    for (let i = 1; i <= totalPages; i++) {
      pdf.setPage(i);
      pdf.setFontSize(9);
      pdf.text(`Page ${i} of ${totalPages}`, pageWidth / 2, pageHeight - 8, { align: "center" });
    }

    pdf.save(`Student_Report_${moment().format("DD-MM-YYYY")}.pdf`);
    setSelectedStudents([]);
  };

  const downloadExcel = () => {
    let studentsToReport = selectedStudents.length > 0
      ? filteredBody.filter(s => selectedStudents.includes(s.student_info.student_addmission_id))
      : filteredBody;

    if (studentsToReport.length === 0) return toast.warning("No data to export");

    const exportHeaders = headerOrder
      .filter((prop) => visibleColumns.includes(prop))
      .map((prop) => allHeaders.find((h) => h.prop === prop))
      .filter(Boolean);

    const excelData = studentsToReport.map((row, index) => {
      let rowData = {};
      exportHeaders.forEach(h => {
        // Enforces table UI naming priority for the column header
        let headerName = (typeof h.title === 'string' ? h.title : h.displayName) || h.prop;
        rowData[headerName] = getRowData(row, h, index);
      });
      return rowData;
    });

    const worksheet = XLSX.utils.json_to_sheet(excelData);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Students Data");
    XLSX.writeFile(workbook, `Student_Export_${moment().format("DD-MM-YYYY")}.xlsx`);

    setSelectedStudents([]);
  };

  const downloadCSV = () => {
    let studentsToReport = selectedStudents.length > 0
      ? filteredBody.filter(s => selectedStudents.includes(s.student_info.student_addmission_id))
      : filteredBody;

    if (studentsToReport.length === 0) return toast.warning("No data to export");

    const exportHeaders = headerOrder
      .filter((prop) => visibleColumns.includes(prop))
      .map((prop) => allHeaders.find((h) => h.prop === prop))
      .filter(Boolean);

    // Creates headers prioritizing UI string titles instead of exclusively displayName
    let csvContent = exportHeaders.map(h => {
      let headerName = (typeof h.title === 'string' ? h.title : h.displayName) || h.prop;
      return `"${headerName.replace(/"/g, '""')}"`;
    }).join(",") + "\n";

    studentsToReport.forEach((row, index) => {
      let rowData = exportHeaders.map(h => {
        let val = getRowData(row, h, index);
        val = String(val).replace(/"/g, '""');
        return `"${val}"`;
      });
      csvContent += rowData.join(",") + "\n";
    });

    // We explicitly append '\uFEFF' to help Microsoft Excel read the CSV correctly without corrupting characters
    const blob = new Blob(["\uFEFF" + csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement("a");
    const url = URL.createObjectURL(blob);
    link.setAttribute("href", url);
    link.setAttribute("download", `Student_Export_${moment().format("DD-MM-YYYY")}.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);

    setSelectedStudents([]);
  };

  const labels = { beforeSelect: " " };

  const StudentAddmissionregistration = () => {
    navigate(`/student/registration`);
  };

  const handleSortDrop = () => {
    const copyListItems = [...headerOrder];
    const dragItemContent = copyListItems[dragItem.current];

    copyListItems.splice(dragItem.current, 1);
    copyListItems.splice(dragOverItem.current, 0, dragItemContent);

    dragItem.current = null;
    dragOverItem.current = null;

    setHeaderOrder(copyListItems);

    setVisibleColumns([
      "selection",
      ...copyListItems.filter((prop) => visibleColumns.includes(prop)),
    ]);

    toast.success("Column order updated");
  };

  return (
    <>
      <Helmet>
        <title>{props?.tabName}</title>
      </Helmet>

      <Col lg={3} className="mx-4" style={{ marginTop: "18px" }}>
        <Link className="nav-link" to="/" style={{ marginLeft: "1px", marginRight: "1px" }}>
          Home <i className="fa-solid fa-chevron-right"></i> Students Addmission
        </Link>
      </Col>

      <ToastContainer />
      <Row className="g-0">
        <Col lg={12} className="px-lg-4">
          {body ? (
            <DatatableWrapper
              key={classSortOrder}
              body={filteredBody}
              headers={tableHeaders}
              paginationOptionsProps={{
                initialState: {
                  rowsPerPage: 50,
                  options: [50, 100, 200, 500, 1000],
                },
              }}
            >
              <ScrollLazyLoader maxRows={1000} step={50} />
              <Row className="mb-2">
                <Col xs={12} lg={2} className="d-flex flex-col justify-content-end align-items-end">
                  <Filter />
                </Col>
                <Col xs={12} lg={1} className="d-flex flex-col justify-content-end align-items-end ">
                  <select
                    className="form-select"
                    value={selectedClass}
                    onChange={handleClassChange}
                    style={{ borderRadius: "5px", width: "100%" }}
                  >
                    <option value="">Select Class</option>
                    {optionClasses.map((className, index) => (
                      <option key={index} value={className.value}>{className.label}</option>
                    ))}
                  </select>
                </Col>

                <Col xs={12} lg={1} className="d-flex flex-col justify-content-end align-items-end ">
                  <select
                    className="form-select"
                    value={selectedSection}
                    onChange={handleSectionChange}
                    disabled={!isSectionEnabled}
                    style={{
                      borderRadius: "5px",
                      backgroundColor: !isSectionEnabled ? "#e9ecef" : "white",
                      cursor: !isSectionEnabled ? "not-allowed" : "pointer",
                    }}
                  >
                    <option value="">Section</option>
                    {optionSection.map((section, index) => (
                      <option key={index} value={section.value}>{section.label}</option>
                    ))}
                  </select>
                </Col>
                {/* status */}
                <Col xs={12} lg={2} className="d-flex flex-col justify-content-end align-items-end ">
                  <select
                    className="form-select"
                    value={selectedStatusFilter}
                    onChange={handleStatusFilterChange}
                    style={{ borderRadius: "5px", width: "100%" }}
                    aria-label="Filter by student status"
                  >
                    <option value="all">All Students</option>
                    <option value="dropout">Dropout</option>
                    <option value="present">Present</option>
                    <option value="inactive">Inactive</option>
                  </select>
                </Col>

                <Col xs={12} sm={5} lg={2} className="d-flex flex-row justify-content-start align-items-start gap-2">
                  <PaginationOptions labels={labels} />
                </Col>

                <Col xs={12} lg={4} className="d-flex justify-content-end align-items-center gap-2 mt-3 ">
                  {(userInfo?.userrole === "SYS_ADMIN" || userInfo?.userrole === "ADMIN" || canCreate) && (
                    <Button
                      className="btn add-new-button"
                      variant=""
                      disabled={!isCurrentSession}
                      onClick={() => StudentAddmissionregistration(true)}
                      style={{ padding: "6px 12px", fontSize: "13px", whiteSpace: "nowrap" }}
                    >
                      New Admission
                    </Button>
                  )}
                  <div className="dropdown">
                    <Button
                      className="btn btn-primary dropdown-toggle"
                      disabled={isDownloading}
                      style={{ padding: "6px 12px", fontSize: "13px", whiteSpace: "nowrap" }}
                      data-bs-toggle="dropdown"
                      aria-expanded="false"
                    >
                      {isDownloading ? "Generating..." : <><i className="fa-solid fa-download"></i> Download</>}
                    </Button>
                    <ul className="dropdown-menu">

                      {/* ID CARD DOWNLOAD SUBMENU */}
                      <li className="position-relative group-submenu">
                        <button type="button" className="dropdown-item d-flex justify-content-between align-items-center" onClick={() => downloadIDCards()}>
                          <span className="d-flex align-items-center gap-2">
                            <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="currentColor" viewBox="0 0 16 16">
                              <path d="M2 2h12v3H2V2zm0 4h12v3H2V6zm0 4h12v3H2v-3z" />
                            </svg> Download Id Card
                          </span>
                          <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" fill="currentColor" viewBox="0 0 16 16">
                            <path fillRule="evenodd" d="M10.854 8.354a.5.5 0 0 0 0-.708L7.672 4.464a.5.5 0 1 0-.708.708L9.793 8l-2.829 2.828a.5.5 0 1 0 .708.708l3.182-3.182z" />
                          </svg>
                        </button>
                        <ul className="dropdown-menu submenu">
                          <li>
                            <button
                              type="button"
                              className="dropdown-item d-flex align-items-center gap-2 text-success fw-bold"
                              onClick={() => downloadIDCards()}
                            >
                              <i className="fa-solid fa-wand-magic-sparkles me-2"></i> Active Saved Design ({(settingsList.find(s => s.key === "id_card_active_orientation")?.value || orientation || "vertical").toUpperCase()})
                            </button>
                          </li>
                          <li>
                            <hr className="dropdown-divider my-1" />
                          </li>
                          <li>
                            <button type="button" className="dropdown-item d-flex align-items-center gap-2" onClick={() => downloadIDCards("vertical")}>
                              <i className="fa-solid fa-arrow-up me-2"></i> Vertical
                            </button>
                          </li>
                          <li>
                            <button type="button" className="dropdown-item d-flex align-items-center gap-2" onClick={() => downloadIDCards("horizontal")}>
                              <i className="fa-solid fa-arrow-right me-2"></i> Horizontal
                            </button>
                          </li>
                        </ul>
                      </li>

                      {/* EXPORT REPORT DATA SUBMENU */}
                      <li className="position-relative group-submenu">
                        <button type="button" className="dropdown-item d-flex justify-content-between align-items-center">
                          <span className="d-flex align-items-center gap-2">
                            <i className="fa-solid fa-file-export ms-1 mx-2"></i> Export Report
                          </span>
                          <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" fill="currentColor" viewBox="0 0 16 16">
                            <path fillRule="evenodd" d="M10.854 8.354a.5.5 0 0 0 0-.708L7.672 4.464a.5.5 0 1 0-.708.708L9.793 8l-2.829 2.828a.5.5 0 1 0 .708.708l3.182-3.182z" />
                          </svg>
                        </button>
                        <ul className="dropdown-menu submenu">
                          <li>
                            <button type="button" className="dropdown-item d-flex align-items-center gap-2" onClick={downloadReport}>
                              <i className="fa-solid fa-file-pdf text-danger me-2"></i> PDF Report
                            </button>
                          </li>
                          <li>
                            <button type="button" className="dropdown-item d-flex align-items-center gap-2" onClick={downloadExcel}>
                              <i className="fa-solid fa-file-excel text-success me-2"></i> Excel (XLSX)
                            </button>
                          </li>
                          <li>
                            <button type="button" className="dropdown-item d-flex align-items-center gap-2" onClick={downloadCSV}>
                              <i className="fa-solid fa-file-csv text-info me-2"></i> CSV Format
                            </button>
                          </li>
                        </ul>
                      </li>

                      <li className="position-relative group-submenu">
                        <button type="button" className="dropdown-item d-flex justify-content-between align-items-center">
                          <span className="d-flex align-items-center gap-2">
                            <i className="fa-solid fa-file-alt"></i> Download Report
                          </span>
                          <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" fill="currentColor" viewBox="0 0 16 16">
                            <path fillRule="evenodd" d="M10.854 8.354a.5.5 0 0 0 0-.708L7.672 4.464a.5.5 0 1 0-.708.708L9.793 8l-2.829 2.828a.5.5 0 1 0 .708.708l3.182-3.182z" />
                          </svg>
                        </button>
                        <ul className="dropdown-menu submenu">
                          <li>
                            <button type="button" className="dropdown-item d-flex align-items-center gap-2" onClick={() => handleDownloadReport()}>
                              <i className="fa-solid fa-download me-2"></i> Generate 360 Report
                            </button>
                          </li>
                        </ul>
                      </li>
                    </ul>
                  </div>
                  <div>
                    <Dropdown align="end">
                      <Dropdown.Toggle
                        variant="dark"
                        id="columns-dropdown-table"
                        style={{ color: "white", border: "none", padding: "6px 12px" }}
                      >
                        <i className="fa-solid fa-cog"></i>
                      </Dropdown.Toggle>

                      <Dropdown.Menu style={{ minWidth: 280, padding: 10, maxHeight: "500px", overflowY: "auto" }}>

                        {/* Draggable Column List */}
                        <Dropdown drop="start">
                          <h6 style={{ marginBottom: 10, fontSize: "12px", color: "#999", textAlign: "center" }}>Drag to Reorder · Check to Show</h6>

                          {headerOrder.map((prop, index) => {
                            const col = allHeaders.find(h => h.prop === prop);
                            console.log("Rendering column toggle for:", col);
                            if (
                              !col ||
                              col.prop === "selection" ||
                              col.prop.toLowerCase().includes("id")
                            ) return null;

                            const titleString = typeof col.title === "string" ? col.title : col.displayName;

                            console.log("title-=>",titleString)

                            return (
                              <div
                                key={prop}
                                draggable
                                onDragStart={() => {
                                  dragItem.current = index;
                                }}
                                onDragEnter={() => {
                                  dragOverItem.current = index;
                                }}
                                onDragOver={(e) => e.preventDefault()}
                                onDrop={handleSortDrop}
                                onDragEnd={(e) => {
                                  e.currentTarget.style.opacity = "1";
                                }}
                                style={{
                                  padding: "8px 6px",
                                  display: "flex",
                                  alignItems: "center",
                                  borderBottom: "1px solid #f0f0f0",
                                  backgroundColor: "white",
                                  cursor: "grab",
                                  transition: "all 0.2s ease",
                                  userSelect: "none",
                                  borderRadius: "3px",
                                  marginBottom: "2px"
                                }}
                                onMouseEnter={(e) => {
                                  e.currentTarget.style.backgroundColor = "#f9f9f9";
                                }}
                                onMouseLeave={(e) => {
                                  e.currentTarget.style.backgroundColor = "white";
                                }}
                              >
                                {/* Drag Handle */}
                                <i
                                  className="fa-solid fa-grip-vertical"
                                  style={{
                                    color: "#ccc",
                                    cursor: "grab",
                                    marginRight: "8px",
                                    fontSize: "12px",
                                    flexShrink: 0
                                  }}
                                />

                                {/* Checkbox */}
                                <input
                                  type="checkbox"
                                  checked={visibleColumns.includes(col.prop)}
                                  onChange={() => toggleColumn(col.prop)}
                                  onClick={(e) => e.stopPropagation()}
                                  id={`col-toggle-table-${col.prop}`}
                                  style={{ cursor: "pointer", marginRight: "8px", flexShrink: 0 }}
                                />

                                {/* Label */}
                                <label
                                  htmlFor={`col-toggle-table-${col.prop}`}
                                  style={{
                                    margin: 0,
                                    cursor: "pointer",
                                    fontSize: "13px",
                                    flexGrow: 1,
                                    userSelect: "none"
                                  }}
                                >
                                  {titleString}
                                  {!visibleColumns.includes(col.prop) && (
                                    <span
                                      style={{
                                        fontSize: "10px",
                                        background: "#eee",
                                        padding: "2px 6px",
                                        borderRadius: "10px",
                                        marginLeft: "6px",
                                      }}
                                    >
                                      Hidden
                                    </span>
                                  )}
                                </label>
                              </div>
                            );
                          })}
                        </Dropdown>
                      </Dropdown.Menu>
                    </Dropdown>
                  </div>
                </Col>
              </Row>

              {/* <div style={{ position: "relative", overflowX: "auto", }}>
                <Table striped className="data-table student-checkbox-table" 
                style={{ width: "max-content",}}
                > */}
              <div className="table-responsive" >
                <Table striped className="student-checkbox-table data-table student-checkbox-table" >
                  <DragDropContext onDragEnd={handleColumnDragEnd}>
                    <Droppable droppableId="table-header" direction="horizontal" type="COLUMN">
                      {(provided, snapshot) => (
                        <thead
                          ref={provided.innerRef}
                          {...provided.droppableProps}
                          style={{
                            ...provided.droppableProps.style,
                            backgroundColor: snapshot.isDraggingOver ? "#e8f4f8" : "#f8f9fa",
                            transition: "background-color 0.2s ease",
                          }}
                        >
                          <tr>
                            {/* Checkbox Column */}
                            {hasVisibleDataColumns && (
                              <th style={{ width: "50px", textAlign: "center", userSelect: "none" }}>
                                <input
                                  type="checkbox"
                                  checked={
                                    filteredBody.length > 0 &&
                                    selectedStudents.length === filteredBody.length
                                  }
                                  onChange={handleSelectAll}
                                  style={{ cursor: "pointer", width: "18px", height: "18px" }}
                                />
                              </th>
                            )}

                            {/* Draggable Column Headers */}
                            {visibleColumns.map((colProp, index) => {
                              const header = allHeaders.find(h => h.prop === colProp);
                              if (!header || header.prop === "selection") return null;

                              return (
                                <Draggable
                                  key={colProp}
                                  draggableId={colProp}
                                  index={index}
                                >
                                  {(provided, snapshot) => (
                                    <th
                                      ref={provided.innerRef}
                                      {...provided.draggableProps}
                                      {...provided.dragHandleProps}
                                      style={{
                                        ...provided.draggableProps.style,
                                        whiteSpace: "nowrap", // Text को नीचे जाने से रोकेगा
                                        minWidth: "150px",    // हर column को कम से कम इतनी जगह मिलेगी
                                        padding: "12px 8px",
                                        backgroundColor: snapshot.isDragging ? "#bbdefb" : "transparent",
                                        cursor: "move",
                                        userSelect: "none",
                                        opacity: snapshot.isDragging ? 0.8 : 1,
                                        transition: "all 0.2s ease",
                                        fontWeight: 600,
                                        color: "#333",
                                        borderBottom: "2px solid #dee2e6",
                                      }}
                                    >
                                      {header.prop === "student_info.class_name" ? (
                                        <div
                                          onClick={(e) => {
                                            e.stopPropagation();
                                            setClassSortOrder(prev => prev === "none" ? "asc" : prev === "asc" ? "desc" : "none");
                                          }}
                                          onMouseDown={(e) => e.stopPropagation()}
                                          style={{ display: "flex", alignItems: "center", gap: "6px", cursor: "pointer" }}
                                          title="Click to sort"
                                        >
                                          <i className="fa-solid fa-arrows-left-right" style={{ opacity: 0.5 }}></i>
                                          {typeof header.title === "string" ? header.title : header.displayName}
                                          {classSortOrder === "asc" ? <i className="fa-solid fa-arrow-down ms-1 text-primary"></i> :
                                            classSortOrder === "desc" ? <i className="fa-solid fa-arrow-up ms-1 text-primary"></i> :
                                              <i className="fa-solid fa-sort ms-1" style={{ opacity: 0.3 }}></i>}
                                        </div>
                                      ) : (
                                        <span style={{ display: "flex", alignItems: "center", gap: "6px" }}>
                                          <i className="fa-solid fa-arrows-left-right" style={{ opacity: 0.5 }}></i>
                                          {typeof header.title === "string" ? header.title : header.displayName}
                                        </span>
                                      )}
                                    </th>
                                  )}
                                </Draggable>
                              );
                            })}
                            {provided.placeholder}
                          </tr>
                        </thead>
                      )}
                    </Droppable>
                  </DragDropContext>
                  <TableBody />
                </Table>
              </div>
              <Pagination />
            </DatatableWrapper>
          ) : (
            <ShimmerTable row={10} col={8} />
          )}
        </Col>
        <Col lg={2}></Col>
      </Row>
    </>
  );
};

export default StudentAddmissionList;
